import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { CalendarIcon, CheckCircle, Users, Clock } from 'lucide-react';
import PublicLayout from '../../components/shared/PublicLayout';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { getApiBase } from '../../lib/api';

const API = getApiBase();

export default function ReservationPage() {
  const [date, setDate] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', time: '', guests: 2, comment: '', seating_preference: 'interieur' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/settings`).then(r => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!settings) return;

    const availablePreferences = [];
    if (settings.reservation_inside_enabled !== false) {
      availablePreferences.push('interieur');
    }
    if (settings.reservation_terrace_enabled !== false) {
      availablePreferences.push('terrasse');
    }

    if (availablePreferences.length === 0) {
      return;
    }

    if (!availablePreferences.includes(form.seating_preference)) {
      setForm(prev => ({ ...prev, seating_preference: availablePreferences[0] }));
    }
  }, [settings, form.seating_preference]);

  const toMinutes = (value) => {
    const [hours, minutes] = String(value || '00:00').split(':').map(Number);
    return (hours * 60) + minutes;
  };

  const timeSlots = [];
  if (settings) {
    const start = toMinutes(settings.reservation_hours_start || '11:30');
    const end = toMinutes(settings.reservation_last_time || settings.reservation_hours_end || '21:00');
    const breakStart = toMinutes(settings.reservation_midday_break_start || '15:30');
    const breakEnd = toMinutes(settings.reservation_midday_break_end || '19:00');
    const step = Number(settings.reservation_slot_minutes || 30);
    for (let minutes = start; minutes <= end; minutes += step) {
      if (minutes >= breakStart && minutes < breakEnd) continue;
      const hh = String(Math.floor(minutes / 60)).padStart(2, '0');
      const mm = String(minutes % 60).padStart(2, '0');
      timeSlots.push(`${hh}:${mm}`);
    }
  }

  const seatingOptions = [];
  if (settings?.reservation_inside_enabled !== false) {
    seatingOptions.push({ value: 'interieur', label: 'Interieur' });
  }
  if (settings?.reservation_terrace_enabled !== false) {
    seatingOptions.push({ value: 'terrasse', label: 'Terrasse' });
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date || !form.time || !form.name || !form.phone || !form.email) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (settings?.reservations_enabled === false) {
      toast.error('Les reservations sont temporairement indisponibles');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/reservations`, {
        ...form,
        date: format(date, 'yyyy-MM-dd'),
        guests: parseInt(form.guests),
      });
      setSubmitted(true);
      toast.success('Reservation envoyee !');
    } catch (err) {
      toast.error('Erreur lors de la reservation');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <PublicLayout>
        <div className="max-w-lg mx-auto px-4 py-24 text-center">
          <CheckCircle className="w-16 h-16 text-[#5B6B3F] mx-auto mb-6" />
          <h1 className="font-heading text-3xl font-bold text-[#2A2A28] mb-4">Reservation envoyee !</h1>
          <p className="text-[#7A7A75] mb-8">Nous avons bien recu votre demande de reservation. Vous recevrez une confirmation prochainement.</p>
          <Button onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', time: '', guests: 2, comment: '', seating_preference: 'interieur' }); setDate(null); }} className="rounded-full bg-[#5B6B3F] hover:bg-[#4A5832] text-white" data-testid="new-reservation-btn">
            Nouvelle reservation
          </Button>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-medium mb-3">Reservation</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#2A2A28] tracking-tighter">Reservez votre table</h1>
        </div>

        {settings?.reservations_enabled === false && (
          <div className="mb-6 rounded-2xl border border-[#8C3A3A]/20 bg-[#8C3A3A]/8 px-4 py-3 text-sm text-[#8C3A3A]">
            Les reservations sont temporairement indisponibles. Merci de revenir plus tard.
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-[#EBE8E0] p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="reservation-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-[#2A2A28] font-medium text-sm">Nom *</Label>
              <Input className="mt-1.5 rounded-xl border-[#EBE8E0] focus:ring-[#5B6B3F]" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="reservation-name" />
            </div>
            <div>
              <Label className="text-[#2A2A28] font-medium text-sm">Telephone *</Label>
              <Input className="mt-1.5 rounded-xl border-[#EBE8E0]" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} required data-testid="reservation-phone" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[#2A2A28] font-medium text-sm">Email *</Label>
              <Input type="email" className="mt-1.5 rounded-xl border-[#EBE8E0]" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required data-testid="reservation-email" />
            </div>

            <div>
              <Label className="text-[#2A2A28] font-medium text-sm mb-1.5 flex items-center gap-1"><CalendarIcon className="w-3.5 h-3.5" /> Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start rounded-xl border-[#EBE8E0] text-left font-normal" data-testid="reservation-date">
                    {date ? format(date, 'dd/MM/yyyy') : 'Choisir une date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label className="text-[#2A2A28] font-medium text-sm mb-1.5 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Heure *</Label>
              <select
                className="w-full h-10 px-3 rounded-xl border border-[#EBE8E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#5B6B3F]/20"
                value={form.time}
                onChange={e => setForm({...form, time: e.target.value})}
                required
                data-testid="reservation-time"
              >
                <option value="">Choisir</option>
                {timeSlots.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div>
              <Label className="text-[#2A2A28] font-medium text-sm mb-1.5 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Nombre de personnes *</Label>
              <select
                className="w-full h-10 px-3 rounded-xl border border-[#EBE8E0] bg-white text-sm"
                value={form.guests}
                onChange={e => setForm({...form, guests: e.target.value})}
                data-testid="reservation-guests"
              >
                {[...Array(Number(settings?.reservation_max_guests || 30))].map((_, i) => <option key={i+1} value={i+1}>{i+1} {i === 0 ? 'personne' : 'personnes'}</option>)}
              </select>
            </div>

            <div>
              <Label className="text-[#2A2A28] font-medium text-sm">Preference *</Label>
              <select
                className="w-full h-10 px-3 rounded-xl border border-[#EBE8E0] bg-white text-sm"
                value={form.seating_preference}
                onChange={e => setForm({...form, seating_preference: e.target.value})}
                data-testid="reservation-seating"
                disabled={seatingOptions.length === 0}
              >
                {seatingOptions.length === 0 && <option value="">Aucune zone disponible</option>}
                {seatingOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div className="sm:col-span-2">
              <Label className="text-[#2A2A28] font-medium text-sm">Commentaire</Label>
              <Textarea className="mt-1.5 rounded-xl border-[#EBE8E0] min-h-[80px]" value={form.comment} onChange={e => setForm({...form, comment: e.target.value})} placeholder="Occasion speciale, allergies..." data-testid="reservation-comment" />
            </div>
          </div>

          <Button type="submit" disabled={loading || settings?.reservations_enabled === false} className="w-full mt-6 rounded-xl bg-[#5B6B3F] hover:bg-[#4A5832] text-white h-12 text-base font-medium" data-testid="reservation-submit">
            {loading ? 'Envoi en cours...' : 'Envoyer ma reservation'}
          </Button>
        </form>
      </div>
    </PublicLayout>
  );
}
